import { useEffect, useRef, useState } from 'react';
import { project, greatCirclePoints, arcToPolylines } from '../utils/arcUtils';

interface Props {
  lat: number;
  lon: number;
  tempLat?: number;
  tempLon?: number;
}

type Ring = number[][];

function ringToPath(ring: Ring): string {
  return ring
    .map(([lon, lat], i) => {
      const [x, y] = project(lon, lat);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ') + ' Z';
}

function featuresToPaths(features: GeoJSON.Feature[]): string[] {
  const paths: string[] = [];
  for (const f of features) {
    const g = f.geometry as GeoJSON.Geometry;
    if (!g) continue;
    if (g.type === 'Polygon') {
      paths.push(ringToPath(g.coordinates[0] as Ring));
    } else if (g.type === 'MultiPolygon') {
      for (const poly of g.coordinates) {
        paths.push(ringToPath(poly[0] as Ring));
      }
    }
  }
  return paths;
}

let cachedPaths: string[] | null = null;
let loadPromise: Promise<string[]> | null = null;

function loadPaths(): Promise<string[]> {
  if (cachedPaths) return Promise.resolve(cachedPaths);
  if (loadPromise) return loadPromise;
  loadPromise = fetch('/world-countries.json')
    .then(r => r.json())
    .then((data: GeoJSON.FeatureCollection) => {
      cachedPaths = featuresToPaths(data.features);
      return cachedPaths;
    });
  return loadPromise;
}

export default function ProfileMapPin({ lat, lon, tempLat, tempLon }: Props) {
  const [paths, setPaths] = useState<string[] | null>(cachedPaths);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!cachedPaths) {
      loadPaths().then(p => { if (mounted.current) setPaths(p); });
    }
    return () => { mounted.current = false; };
  }, []);

  const [pinX, pinY] = project(lon, lat);
  const W = 960;
  const H = 480;
  const VIEW_TOP = 27;
  const VIEW_BOTTOM = 400;
  const VIEW_H = VIEW_BOTTOM - VIEW_TOP;
  const clampedPinX = Math.max(4, Math.min(W - 4, pinX));
  const clampedPinY = Math.max(VIEW_TOP + 4, Math.min(VIEW_BOTTOM - 4, pinY));

  const hasTempLoc = tempLat !== undefined && tempLon !== undefined;
  const [tempX, tempY] = hasTempLoc ? project(tempLon!, tempLat!) : [0, 0];
  const clampedTempX = Math.max(4, Math.min(W - 4, tempX));
  const clampedTempY = Math.max(VIEW_TOP + 4, Math.min(VIEW_BOTTOM - 4, tempY));

  const arcPolylines = hasTempLoc
    ? arcToPolylines(greatCirclePoints(lon, lat, tempLon!, tempLat!))
    : [];

  return (
    <svg
      viewBox={`0 ${VIEW_TOP} ${W} ${VIEW_H}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', width: '100%', height: '100%' }}
      aria-hidden="true"
    >
      {/* World outline */}
      {paths && (
        <g fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.35)" strokeWidth="0.6">
          {paths.map((d, i) => <path key={i} d={d} />)}
        </g>
      )}

      {/* Great-circle arc between home and current location */}
      {arcPolylines.map((pts, i) => (
        <polyline
          key={i}
          points={pts}
          fill="none"
          stroke="rgba(255,255,255,0.75)"
          strokeWidth="1.5"
          strokeDasharray="6 5"
          strokeLinecap="round"
        />
      ))}

      {/* Home location pin */}
      <g transform={`translate(${clampedPinX},${clampedPinY})`}>
        <circle r="14" fill="rgba(255,255,255,0.08)" />
        <circle r="9" fill="white" fillOpacity="0.95" />
        <path
          d="M1 -4.5 L-2.5 0.5 H0.5 L-1 4.5 L3 -0.5 H0.2 Z"
          fill="none"
          stroke="rgba(0,0,0,0.55)"
          strokeWidth="1.1"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </g>

      {/* Current / temporary location pin */}
      {hasTempLoc && (
        <g transform={`translate(${clampedTempX},${clampedTempY})`}>
          <circle r="14" fill="rgba(255,255,255,0.08)" />
          <circle r="9" fill="white" fillOpacity="0.85" />
          {/* Navigation arrow icon */}
          <path
            d="M0 -5 L3.5 4 L0 2 L-3.5 4 Z"
            fill="rgba(0,0,0,0.5)"
            stroke="none"
          />
        </g>
      )}
    </svg>
  );
}
